import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import cls from 'clsx';
import { observer } from 'mobx-react-lite';
import type { CurrencyCurrency } from '@linktivity/ars-neo';
import { Down, Info } from '@linktivity/link-icons';
import { Button } from '@linktivity/link-ui';
import type { PaymentSectionInfo } from '@link/share/components/Payment';
import Price from '@link/share/components/Price';
import { getOptionUnitRestrictionMessageDescriptor } from '@link/share/utils';
import { useBookingStore } from '../context';
import styles from './bookingPayment.module.css';

/** Where the breakdown starts showing by default; see `.breakdown`. */
const WIDE_SCREEN = '(min-width: 768px)';

interface Props {
  onSubmit: () => void;
}

/**
 * The booking's summary box: what is being bought, the total, and the submit
 * button. Sticky beside the page on wide screens.
 *
 * Renders on the server: before the calendar has loaded it shows the opening
 * plan with a zero total and a disabled button, which is what the visitor sees
 * first anyway — and no skeleton to swap out.
 *
 * The breakdown can be folded away. Until the visitor folds or unfolds it
 * (`expanded` undefined), it shows on wide screens and hides on phones —
 * decided by CSS, so the server's HTML is right for both.
 */
export const BookingPayment = observer(function BookingPayment({
  onSubmit
}: Props) {
  const store = useBookingStore();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<boolean>();
  const { paymentData: data, currency } = store;
  const hasBreakdown = !!data.plan || !!data.options?.length;
  const expandedAttr = expanded === undefined ? undefined : String(expanded);

  const restriction = getOptionUnitRestrictionMessageDescriptor(
    store.optionUnitRestrictionResult
  );
  const hint = restriction
    ? t(restriction.key, { quantity: restriction.quantity })
    : store.missingRequiredOption
      ? t('share.payment.optionRequiredTip')
      : '';

  return (
    <div className={styles.panel}>
      {hasBreakdown && (
        <div className={styles.breakdown} data-expanded={expandedAttr}>
          {data.plan && (
            <PaymentSection
              heading={t('share.payment.plan')}
              section={data.plan}
              currency={currency}
            />
          )}
          {data.options?.map((option, index) => (
            <PaymentSection
              key={index}
              heading={t('share.payment.option')}
              section={option}
              currency={currency}
            />
          ))}
        </div>
      )}
      <div className={styles.total}>
        <div className={styles.totalLabel}>
          <p className={styles.totalLabelText}>{t('share.payment.total')}</p>
          {hasBreakdown && (
            <button
              type="button"
              className={styles.toggle}
              aria-expanded={expanded}
              data-expanded={expandedAttr}
              onClick={() =>
                setExpanded(
                  shown => !(shown ?? window.matchMedia(WIDE_SCREEN).matches)
                )
              }
            >
              {t('share.payment.details')}
              <Down className={styles.toggleIcon} />
            </button>
          )}
        </div>
        <Price
          className={styles.totalAmount}
          amount={data.totalAmount}
          currencyCode={currency}
        />
      </div>
      {store.dependentUnitWarningTitle && (
        <PaymentNotice tone="warning">
          {t('share.payment.warn', {
            unitName: store.dependentUnitWarningTitle
          })}
        </PaymentNotice>
      )}
      {hint && <PaymentNotice tone="hint">{hint}</PaymentNotice>}
      <Button
        className={styles.submit}
        disabled={store.orderDisabled || store.bookingLoading}
        loading={store.bookingLoading}
        onClick={onSubmit}
      >
        {t(store.submitLabelKey)}
      </Button>
    </div>
  );
});

/**
 * One thing being bought — the main product or an option — with its plan,
 * when or how long it is good for, and its tickets (大人 ¥3,000 ×2).
 */
function PaymentSection({
  heading,
  section,
  currency
}: {
  /** What kind of thing it is: プラン, オプション. */
  heading: string;
  section: PaymentSectionInfo;
  currency: CurrencyCurrency;
}) {
  const { t } = useTranslation();
  const schedule = section.validityPeriod
    ? {
        label: t('share.payment.validityPeriod'),
        value: section.validityPeriod
      }
    : section.dateTime?.trim()
      ? { label: t('share.payment.dateTime'), value: section.dateTime }
      : undefined;
  return (
    <dl className={styles.section}>
      <div className={styles.field}>
        <dt>{heading}</dt>
        <dd>{section.name}</dd>
      </div>
      {section.planName && (
        <div className={styles.field}>
          <dt>{t('share.payment.plan')}</dt>
          <dd>{section.planName}</dd>
        </div>
      )}
      {schedule && (
        <div className={styles.field}>
          <dt>{schedule.label}</dt>
          <dd>{schedule.value}</dd>
        </div>
      )}
      {section.items.length > 0 && (
        <div className={styles.field}>
          <dt>{t('share.payment.ticket')}</dt>
          {section.items.map(item => (
            <dd key={item.label} className={styles.line}>
              <span className={styles.lineLabel}>{item.label}</span>
              <div className={styles.lineAmount}>
                <Price
                  className={styles.linePrice}
                  amount={item.price}
                  currencyCode={currency}
                />
                <span className={styles.lineQuantity}>
                  {t('share.common.multiply')}
                  {item.quantity}
                </span>
              </div>
            </dd>
          ))}
        </div>
      )}
    </dl>
  );
}

/**
 * Why the booking cannot go on yet, above the button. A `warning` is about
 * the tickets picked; a `hint` names what is still missing.
 */
function PaymentNotice({
  tone,
  children
}: {
  tone: 'warning' | 'hint';
  children: ReactNode;
}) {
  return (
    <p className={cls(styles.notice, styles[tone])}>
      <Info className={styles.noticeIcon} />
      <span>{children}</span>
    </p>
  );
}
